import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { UtensilsCrossed, Plus, Pencil, Trash2, ChevronRight, Tag, X, Check } from 'lucide-react';

// ─── Small inline modal ───────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
            <div className="card" style={{ width: '100%', maxWidth: '420px', padding: '28px', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '1.1rem', fontFamily: 'var(--font-display)' }}>{title}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                        <X size={20} />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MenuCategoriesManager() {
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loadingCats, setLoadingCats] = useState(true);
    const [loadingSubs, setLoadingSubs] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Modal state
    const [modal, setModal] = useState(null); // { type: 'cat' | 'sub', mode: 'add' | 'edit', data?: {} }
    const [formName, setFormName] = useState('');
    const [formIcon, setFormIcon] = useState('');
    const [formOrder, setFormOrder] = useState(0);

    // ── Fetch categories ──────────────────────────────────────────────────────
    const fetchCategories = useCallback(async () => {
        setLoadingCats(true);
        try {
            const { data, error } = await supabase
                .from('menu_categories')
                .select('*')
                .order('sort_order', { ascending: true });
            if (error) throw error;
            setCategories(data || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingCats(false);
        }
    }, []);

    // ── Fetch subcategories for selected category ─────────────────────────────
    const fetchSubcategories = useCallback(async (categoryId) => {
        if (!categoryId) { setSubcategories([]); return; }
        setLoadingSubs(true);
        try {
            const { data, error } = await supabase
                .from('menu_subcategories')
                .select('*')
                .eq('category_id', categoryId)
                .order('sort_order', { ascending: true });
            if (error) throw error;
            setSubcategories(data || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoadingSubs(false);
        }
    }, []);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);
    useEffect(() => { fetchSubcategories(selectedCategory?.id); }, [selectedCategory, fetchSubcategories]);

    // ── Open modal helpers ────────────────────────────────────────────────────
    const openModal = (type, mode, data = null) => {
        setFormName(data?.name || '');
        setFormIcon(data?.icon || '');
        setFormOrder(data?.sort_order ?? 0);
        setModal({ type, mode, data });
    };
    const closeModal = () => { setModal(null); setError(null); };

    // ── Save (create / update) ────────────────────────────────────────────────
    const handleSave = async () => {
        if (!formName.trim()) { setError('Name is required'); return; }
        setSaving(true);
        setError(null);
        try {
            if (modal.type === 'cat') {
                const payload = { name: formName.trim(), icon: formIcon.trim() || null, sort_order: Number(formOrder) };
                if (modal.mode === 'add') {
                    const { error } = await supabase.from('menu_categories').insert(payload);
                    if (error) throw error;
                } else {
                    const { error } = await supabase.from('menu_categories').update(payload).eq('id', modal.data.id);
                    if (error) throw error;
                    if (selectedCategory?.id === modal.data.id) {
                        setSelectedCategory(prev => ({ ...prev, ...payload }));
                    }
                }
                await fetchCategories();
            } else {
                const payload = { name: formName.trim(), sort_order: Number(formOrder), category_id: selectedCategory.id };
                if (modal.mode === 'add') {
                    const { error } = await supabase.from('menu_subcategories').insert(payload);
                    if (error) throw error;
                } else {
                    const { error } = await supabase.from('menu_subcategories').update({ name: formName.trim(), sort_order: Number(formOrder) }).eq('id', modal.data.id);
                    if (error) throw error;
                }
                await fetchSubcategories(selectedCategory.id);
            }
            closeModal();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Toggle active ─────────────────────────────────────────────────────────
    const toggleActive = async (table, id, current) => {
        await supabase.from(table).update({ is_active: !current }).eq('id', id);
        table === 'menu_categories' ? fetchCategories() : fetchSubcategories(selectedCategory?.id);
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (table, id, name) => {
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
        const { error } = await supabase.from(table).delete().eq('id', id);
        if (error) { alert(error.message); return; }
        if (table === 'menu_categories') {
            if (selectedCategory?.id === id) { setSelectedCategory(null); setSubcategories([]); }
            fetchCategories();
        } else {
            fetchSubcategories(selectedCategory?.id);
        }
    };

    // ── Shared row style ──────────────────────────────────────────────────────
    const rowStyle = (isSelected) => ({
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '12px 16px', borderRadius: '8px', marginBottom: '6px',
        background: isSelected ? 'var(--accent-primary-light, rgba(99,102,241,0.12))' : 'var(--bg-card)',
        border: `1px solid ${isSelected ? 'var(--accent-primary)' : 'var(--border-light)'}`,
        cursor: 'pointer', transition: 'all 0.15s',
    });

    const iconBtnStyle = { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center' };

    return (
        <div>
            {/* ── Header ── */}
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ fontSize: '1.75rem', fontFamily: 'var(--font-display)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <UtensilsCrossed color="var(--accent-primary)" /> Menu Categories
                </h1>
                <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Manage the categories and subcategories that restaurants use when adding menu items.
                </p>
            </div>

            {/* ── Two-column layout ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

                {/* Left: Categories */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>
                            Categories <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: '0.85rem' }}>({categories.length})</span>
                        </h2>
                        <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                            onClick={() => openModal('cat', 'add')}>
                            <Plus size={15} /> Add Category
                        </button>
                    </div>

                    {loadingCats ? (
                        <p style={{ color: 'var(--text-secondary)', padding: '16px' }}>Loading…</p>
                    ) : categories.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', padding: '16px' }}>No categories yet. Add one to get started.</p>
                    ) : (
                        categories.map(cat => (
                            <div key={cat.id} style={rowStyle(selectedCategory?.id === cat.id)} onClick={() => setSelectedCategory(cat)}>
                                <span style={{ fontSize: '1.4rem', minWidth: '28px', textAlign: 'center' }}>{cat.icon || '📋'}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {cat.name}
                                        {!cat.is_active && <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: '99px', background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>inactive</span>}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>order: {cat.sort_order}</div>
                                </div>
                                <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                                    <button style={{ ...iconBtnStyle, color: 'var(--text-secondary)' }} title="Edit" onClick={() => openModal('cat', 'edit', cat)}><Pencil size={14} /></button>
                                    <button style={{ ...iconBtnStyle, color: cat.is_active ? '#f59e0b' : '#22c55e' }} title={cat.is_active ? 'Deactivate' : 'Activate'}
                                        onClick={() => toggleActive('menu_categories', cat.id, cat.is_active)}>
                                        {cat.is_active ? <X size={14} /> : <Check size={14} />}
                                    </button>
                                    <button style={{ ...iconBtnStyle, color: '#ef4444' }} title="Delete" onClick={() => handleDelete('menu_categories', cat.id, cat.name)}><Trash2 size={14} /></button>
                                </div>
                                <ChevronRight size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            </div>
                        ))
                    )}
                </div>

                {/* Right: Subcategories */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>
                            {selectedCategory
                                ? <><span style={{ marginRight: '4px' }}>{selectedCategory.icon}</span>{selectedCategory.name} — Subcategories <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: '0.85rem' }}>({subcategories.length})</span></>
                                : 'Subcategories'}
                        </h2>
                        {selectedCategory && (
                            <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}
                                onClick={() => openModal('sub', 'add')}>
                                <Plus size={15} /> Add
                            </button>
                        )}
                    </div>

                    {!selectedCategory ? (
                        <div className="card" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <Tag size={32} style={{ margin: '0 auto 12px', display: 'block', opacity: 0.4 }} />
                            <p>Select a category on the left to manage its subcategories.</p>
                        </div>
                    ) : loadingSubs ? (
                        <p style={{ color: 'var(--text-secondary)', padding: '16px' }}>Loading…</p>
                    ) : subcategories.length === 0 ? (
                        <div className="card" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <p>No subcategories yet.</p>
                            <button className="btn btn-primary" style={{ marginTop: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                onClick={() => openModal('sub', 'add')}>
                                <Plus size={14} /> Add First Subcategory
                            </button>
                        </div>
                    ) : (
                        subcategories.map(sub => (
                            <div key={sub.id} style={{ ...rowStyle(false), cursor: 'default' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 500, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {sub.name}
                                        {!sub.is_active && <span style={{ fontSize: '0.7rem', padding: '2px 6px', borderRadius: '99px', background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}>inactive</span>}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>order: {sub.sort_order}</div>
                                </div>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                    <button style={{ ...iconBtnStyle, color: 'var(--text-secondary)' }} title="Edit" onClick={() => openModal('sub', 'edit', sub)}><Pencil size={14} /></button>
                                    <button style={{ ...iconBtnStyle, color: sub.is_active ? '#f59e0b' : '#22c55e' }} title={sub.is_active ? 'Deactivate' : 'Activate'}
                                        onClick={() => toggleActive('menu_subcategories', sub.id, sub.is_active)}>
                                        {sub.is_active ? <X size={14} /> : <Check size={14} />}
                                    </button>
                                    <button style={{ ...iconBtnStyle, color: '#ef4444' }} title="Delete" onClick={() => handleDelete('menu_subcategories', sub.id, sub.name)}><Trash2 size={14} /></button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* ── Modal ── */}
            {modal && (
                <Modal
                    title={modal.mode === 'add'
                        ? (modal.type === 'cat' ? 'Add Category' : `Add Subcategory to "${selectedCategory?.name}"`)
                        : (modal.type === 'cat' ? 'Edit Category' : 'Edit Subcategory')}
                    onClose={closeModal}
                >
                    {error && <p style={{ color: '#ef4444', marginBottom: '12px', fontSize: '0.85rem' }}>{error}</p>}

                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Name *</label>
                        <input
                            className="form-input"
                            placeholder={modal.type === 'cat' ? 'e.g. Drinks' : 'e.g. Beer'}
                            value={formName}
                            onChange={e => setFormName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSave()}
                            autoFocus
                        />
                    </div>

                    {modal.type === 'cat' && (
                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Icon (emoji)</label>
                            <input
                                className="form-input"
                                placeholder="e.g. 🍺"
                                value={formIcon}
                                onChange={e => setFormIcon(e.target.value)}
                            />
                        </div>
                    )}

                    <div style={{ marginBottom: '24px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '6px' }}>Sort Order</label>
                        <input
                            className="form-input"
                            type="number"
                            min="0"
                            value={formOrder}
                            onChange={e => setFormOrder(e.target.value)}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button className="btn" style={{ flex: 1 }} onClick={closeModal}>Cancel</button>
                        <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : modal.mode === 'add' ? 'Create' : 'Save Changes'}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
